import { createContext, useContext, useState, ReactNode } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export interface UnifiedRole {
    role: string; // matches a Mongoose model name (Admin, Reviewer, ManagingEditor, ...)
    profileId: string;
    fullName: string | null;
    email: string | null;
}

interface UnifiedUser {
    id: string;
    email: string | null;
    username: string | null;
    fullName: string | null;
}

interface UnifiedAuthContextType {
    user: UnifiedUser | null;
    roles: UnifiedRole[];
    loading: boolean;
    error: string | null;
    login: (identifier: string, password: string) => Promise<void>;
    logout: () => void;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType>({
    user: null,
    roles: [],
    loading: false,
    error: null,
    login: async () => { },
    logout: () => { }
});

export const useUnifiedAuth = () => useContext(UnifiedAuthContext);

// Maps each role to the exact localStorage keys (and payload shape) its existing
// standalone login page already writes, so every existing per-role Context /
// ProtectedRoute / Dashboard keeps working completely unchanged after this login.
const ROLE_STORAGE: Record<string, { tokenKey: string; dataKey: string; shape: 'raw' | 'json' }> = {
    Admin: { tokenKey: 'adminToken', dataKey: 'adminUsername', shape: 'raw' },
    Reviewer: { tokenKey: 'reviewerToken', dataKey: 'reviewerInfo', shape: 'json' },
    Editor: { tokenKey: 'editorToken', dataKey: 'editorData', shape: 'json' },
    ManagingEditor: { tokenKey: 'managingEditorToken', dataKey: 'managingEditorData', shape: 'json' },
    AssociateEditor: { tokenKey: 'associateEditorToken', dataKey: 'associateEditorData', shape: 'json' },
    EditorInChief: { tokenKey: 'editorInChiefToken', dataKey: 'editorInChiefData', shape: 'json' },
    EthicsQualityEditor: { tokenKey: 'ethicsQualityEditorToken', dataKey: 'ethicsQualityEditorData', shape: 'json' },
    EditorialOffice: { tokenKey: 'editorialOfficeToken', dataKey: 'editorialOfficeData', shape: 'json' },
    EditorialAssistant: { tokenKey: 'editorialAssistantToken', dataKey: 'editorialAssistantData', shape: 'json' },
    SectionEditor: { tokenKey: 'sectionEditorToken', dataKey: 'sectionEditorData', shape: 'json' }
};

export const ROLE_LABELS: Record<string, string> = {
    Admin: 'Admin',
    Reviewer: 'Reviewer',
    Editor: 'Production Editor',
    ManagingEditor: 'Managing Editor',
    AssociateEditor: 'Associate Editor',
    EditorInChief: 'Editor-in-Chief',
    EthicsQualityEditor: 'Ethics & Quality Editor',
    EditorialOffice: 'Editorial Office',
    EditorialAssistant: 'Editorial Assistant',
    SectionEditor: 'Section Editor'
};

function clearAllRoleStorage() {
    Object.values(ROLE_STORAGE).forEach(({ tokenKey, dataKey }) => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(dataKey);
    });
    localStorage.removeItem('unifiedUser');
    localStorage.removeItem('unifiedRoles');
}

function readStoredUser(): UnifiedUser | null {
    const stored = localStorage.getItem('unifiedUser');
    return stored ? JSON.parse(stored) : null;
}

function readStoredRoles(): UnifiedRole[] {
    const stored = localStorage.getItem('unifiedRoles');
    return stored ? JSON.parse(stored) : [];
}

export const UnifiedAuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<UnifiedUser | null>(readStoredUser);
    const [roles, setRoles] = useState<UnifiedRole[]>(readStoredRoles);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const login = async (identifier: string, password: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${API_URL}/api/login`, { identifier, password });
            const { user: returnedUser, roles: returnedRoles, tokens } = response.data as {
                user: UnifiedUser;
                roles: UnifiedRole[];
                tokens: Record<string, string>;
            };

            returnedRoles.forEach((entry) => {
                const config = ROLE_STORAGE[entry.role];
                const token = tokens[entry.role];
                if (!config || !token) return;

                localStorage.setItem(config.tokenKey, token);
                if (config.shape === 'raw') {
                    localStorage.setItem(config.dataKey, returnedUser.username || '');
                } else {
                    localStorage.setItem(config.dataKey, JSON.stringify({
                        _id: entry.profileId,
                        id: entry.profileId,
                        name: entry.fullName,
                        fullName: entry.fullName,
                        email: entry.email
                    }));
                }
            });

            localStorage.setItem('unifiedUser', JSON.stringify(returnedUser));
            localStorage.setItem('unifiedRoles', JSON.stringify(returnedRoles));

            // Full reload: every existing role Context is mounted once, globally, in
            // App.tsx, and only reads localStorage on its own mount -- exactly like each
            // role's own standalone login page, which also does a full-page redirect.
            window.location.href = '/dashboard';
        } catch (err: any) {
            setError(err.response?.data?.message || 'Login failed. Please try again.');
            setLoading(false);
            throw err;
        }
    };

    const logout = () => {
        clearAllRoleStorage();
        setUser(null);
        setRoles([]);
        window.location.href = '/login';
    };

    return (
        <UnifiedAuthContext.Provider value={{ user, roles, loading, error, login, logout }}>
            {children}
        </UnifiedAuthContext.Provider>
    );
};
